import { useMemo } from "react";
import { TrendingUp } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@nosible/shared/components/ui/chart";
import {
  AreaChart,
  Area,
  YAxis,
  CartesianGrid,
} from "recharts";
import { trpc } from "@/admin/lib/trpc";
import { useSession } from "@/admin/lib/auth-client";
import { getQueryTypeColor } from "@nosible/shared/lib";

interface SubTotalUsageProps {
  subscriptionId: string;
}

type UsageDataPoint = {
  date: string;
  timestamp: number;
  total: number;
  [key: string]: string | number;
};

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
  }).format(value);
}

function formatChartDate(timestamp: number): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}

function formatUpdatedAt(value: string | number | Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function UsageSeriesChart({
  data,
  type,
  index,
}: {
  data: UsageDataPoint[];
  type: string;
  index: number;
}) {
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  const color = getQueryTypeColor(type, index);
  const chartConfig: ChartConfig = {
    [type]: { label, color },
  };
  const values = data.map((point) =>
    typeof point[type] === "number" ? point[type] as number : 0
  );
  const currentValue = values.at(-1) ?? 0;
  const maxValue = Math.max(...values, 0);
  const yMax = maxValue > 0 ? maxValue * 1.08 : 1;
  const firstTimestamp = data[0]?.timestamp ?? Date.now();
  const lastTimestamp = data.at(-1)?.timestamp ?? firstTimestamp;
  const gradientId = `usage-${type.replace(/[^a-z0-9]/gi, "-")}-${index}`;

  return (
    <section className="usage-series-panel" aria-label={`${label} request history`}>
      <div className="usage-series-panel__header">
        <div className="usage-series-panel__identity">
          <span style={{ backgroundColor: color }} aria-hidden="true" />
          <div>
            <span>REQUEST TYPE</span>
            <h3>{label}</h3>
          </div>
        </div>
        <div className="usage-series-panel__value">
          <span>CURRENT TOTAL</span>
          <strong>{currentValue.toLocaleString()}</strong>
        </div>
      </div>

      <ChartContainer config={chartConfig} className="usage-series-chart">
        <AreaChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="2 6"
            vertical={false}
            stroke="rgb(87 87 90 / 45%)"
          />
          <YAxis
            width={42}
            domain={[0, yMax]}
            tickCount={3}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            tickFormatter={(value) => formatCompactNumber(Number(value))}
          />
          <Area
            type="monotone"
            dataKey={type}
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 4, fill: color, stroke: "#0e0e10", strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <ChartTooltip
            cursor={{ stroke: "#57575a", strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as UsageDataPoint;
              const value = typeof point[type] === "number" ? point[type] as number : 0;

              return (
                <div className="usage-chart-tooltip">
                  <span>{formatUpdatedAt(point.timestamp)}</span>
                  <strong style={{ color }}>{value.toLocaleString()}</strong>
                  <small>{label.toUpperCase()} REQUESTS</small>
                </div>
              );
            }}
          />
        </AreaChart>
      </ChartContainer>
      <div className="usage-series-panel__range" aria-hidden="true">
        <span>{formatChartDate(firstTimestamp)}</span>
        <span>{formatChartDate(lastTimestamp)}</span>
      </div>
    </section>
  );
}

function TotalUsageChart({ data }: { data: UsageDataPoint[] }) {
  const rateTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== "date" && key !== "timestamp" && key !== "total") {
          types.add(key);
        }
      });
    });
    return Array.from(types);
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-chromium-mist">
        No usage data to display
      </div>
    );
  }

  return (
    <div className="usage-series-list" aria-label="Cumulative request history by type">
      {rateTypes.map((type, index) => (
        <UsageSeriesChart key={type} data={data} type={type} index={index} />
      ))}
    </div>
  );
}

export function SubTotalUsage({ subscriptionId }: SubTotalUsageProps) {
  const { data: session } = useSession();

  const { data: totalUsageHistory } = trpc.subscription.usage.totalUsageHistory.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  return (
    <Card className="total-usage-card">
      <CardHeader>
        <div className="total-usage-card__heading">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Total Usage (All Time)
            </CardTitle>
            <CardDescription>
              Cumulative API usage across all billing periods
            </CardDescription>
          </div>
          {totalUsageHistory?.lastUpdated && (
            <span className="total-usage-card__updated">
              UPDATED {formatUpdatedAt(totalUsageHistory.lastUpdated)}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!totalUsageHistory || totalUsageHistory.history.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-chromium-mist mb-2">No Usage History Yet</div>
            <p className="text-sm text-chromium-mist/70">
              Usage history will appear here after the first recorded request.
            </p>
          </div>
        ) : (
          <div className="total-usage-card__content">
            <div className="usage-total-summary">
              <div>
                <span>ALL REQUEST TYPES</span>
                <strong>{totalUsageHistory.currentTotal.toLocaleString()}</strong>
                <small>TOTAL REQUESTS</small>
              </div>
              <p>
                <span>HISTORY VIEW</span>
                Independent scale per request type
              </p>
            </div>
            <TotalUsageChart data={totalUsageHistory.history} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
